import {
  resolveXlsxFileName,
  triggerBlobDownload,
} from '../../utils/http/responseHelpers';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import qs from 'qs';
import { DispensarizationBatch } from '../../models/MedVox/DispensarizationBatch';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { apiClientWithApi, apiLoaderClientWithApi } from '../../utils/http';
import { newDispensarizationBatchParameters } from '../../hooks/components/useDispensarization/constants';

export interface DispensarizationBatchesState {
  dispensarizationBatches: DispensarizationBatch[];
  status: Status;
}

const initialState: DispensarizationBatchesState = {
  dispensarizationBatches: [],
  status: {
    responseLoading: true,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

export type DispensarizationsBatchFormData = {
  name: string;
  from: string;
  to: string;
  file: FormData;
};

export const getDispensarizationBatches = createAsyncThunk(
  'DispensarizationBatch/getDispensarizationBatches',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<DispensarizationBatch[]>(
        'task/dispensarization/batch',
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else
        return rejectWithValue(
          'Не удалось получить список кампаний диспансеризации!',
        );
    }
  },
);

export const createDispensarizationBatch = createAsyncThunk(
  'DispensarizationBatch/createDispensarizationBatch',
  async (
    requestFormData: DispensarizationsBatchFormData,
    { rejectWithValue },
  ) => {
    try {
      const response = await apiLoaderClientWithApi.post(
        'task/dispensarization/load/file',
        requestFormData.file,
        {
          params: {
            [newDispensarizationBatchParameters.from]: requestFormData.from,
            [newDispensarizationBatchParameters.to]: requestFormData.to,
            [newDispensarizationBatchParameters.name]: requestFormData.name,
          },
          paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else
        return rejectWithValue(
          'Не удалось получить список кампаний диспансеризации!',
        );
    }
  },
);

export const downloadDispensarizationsBatchTemplate = createAsyncThunk(
  'DispensarizationBatch/downloadDispensarizationsBatchTemplate',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiLoaderClientWithApi.get(
        'task/dispensarization/load/file/template',
        {
          responseType: 'blob',
        },
      );

      const fileName = resolveXlsxFileName(
        response.headers['content-disposition'],
        'Шаблон диспансеризация.xlsx',
      );
      triggerBlobDownload(new Blob([response.data]), fileName);

      return fileName;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else return rejectWithValue('Не удалось получить отчёт!');
    }
  },
);

export const dispensarizationBatchSlice = createSlice({
  name: 'dispensarizationBatch',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getDispensarizationBatches.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(getDispensarizationBatches.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.dispensarizationBatches = action.payload;
        return state;
      })
      .addCase(getDispensarizationBatches.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError =
          'Не удалось получить список кампаний диспансеризации';
      })
      .addCase(createDispensarizationBatch.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(createDispensarizationBatch.fulfilled, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        return state;
      })
      .addCase(createDispensarizationBatch.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось получить создать кампанию';
      })
      .addCase(downloadDispensarizationsBatchTemplate.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(downloadDispensarizationsBatchTemplate.fulfilled, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        return state;
      })
      .addCase(downloadDispensarizationsBatchTemplate.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить шаблон';
        return state;
      });
  },
});

export default dispensarizationBatchSlice.reducer;

export const selectDispensarizationBatches = (state: RootState) =>
  state.dispensarizationBatch.dispensarizationBatches;
export const dispensarizationBatchesStatus = (state: RootState) =>
  state.dispensarizationBatch.status;
